export function getCodeState({ code }: { code: string }): State {
	return codeStateMap[code] ?? DEFAULT;
}

type State = {
	title?: string;
	subtitle?: string;
	message?: string;
};

const DEFAULT: State = {
	title: "System Error",
	message:
		"Could not process the transaction due to a system error. Try processing the transaction again.",
};

const codeStateMap: Record<string, State> = {
	"1": {
		subtitle: "Refer to card issuer (1)",
		message:
			"Transaction declined by issuer. Instruct the cardholder to contact their bank or consider another payment method.",
	},
	"2": {
		subtitle: "Refer to card issuer’s special conditions (2)",
		message:
			"Transaction requires special conditions from the issuer. Instruct the cardholder to contact their bank or consider another payment method.",
	},
	"3": {
		subtitle: "Invalid merchant (3)",
		message:
			"Verify your status with Zeller. If the issue persists, call Zeller Support.",
	},
	"6": {
		subtitle: "Error (6)",
		message:
			"Unexpected transaction error. Please retry or ask the cardholder to contact their bank if the issue persists.",
	},
	"7": {
		subtitle: "Pick‑up card, special condition (7)",
		message:
			"Card reported lost or stolen. Ask the cardholder to contact their bank and consider using a different payment method.",
	},
	"8": {
		subtitle: "Signature Verification Required (8)",
		message:
			"Signature verification required. Please ask the cardholder to reconfirm their signature and retry the transaction.",
	},
	"9": {
		subtitle: "Request in progress (9)",
		message:
			"The terminal may be busy with another transaction. Please wait a moment and retry. If issue persists, restart terminal.",
	},
	"12": {
		subtitle: "Invalid transaction (12)",
		message:
			"Card processing error. Instruct the cardholder to contact their bank or consider another payment method.",
	},
	"13": {
		subtitle: "Invalid amount (13)",
		message:
			"Invalid transaction amount. Check the transaction amount and retry.",
	},
	"14": {
		subtitle: "Invalid card number (14)",
		message:
			"Invalid card number. Ask the cardholder to re‑enter card details and retry.",
	},
	"15": {
		subtitle: "No such issuer (15)",
		message:
			"Issuing bank not recognised. Ask the cardholder to contact their bank or consider a different payment method.",
	},
	"17": {
		subtitle: "Customer cancellation (17)",
		message: "Transaction cancelled. Please retry.",
	},
	"18": {
		subtitle: "Customer dispute (18)",
		message:
			"Charge disputed by customer. Review transaction, gather evidence, and contact Zeller Support for further assistance.",
	},
	"19": {
		subtitle: "Re‑enter transaction (19)",
		message: "Transaction not processed. Please retry.",
	},
	"20": {
		subtitle: "Invalid response (20)",
		message:
			"Unrecognized response. Retry transaction or ask the cardholder to contact their bank if the issue persists.",
	},
	"21": {
		subtitle: "No action taken (21)",
		message:
			"No action from bank. Retry transaction or ask the cardholder to contact their bank if the issue persists.",
	},
	"22": {
		subtitle: "Suspected malfunction (22)",
		message: "Issuing bank not responding. Check card information and retry.",
	},
	"23": {
		subtitle: "Unacceptable transaction fee (23)",
		message:
			"High transaction fee. Consider lower fees or a different payment method.",
	},
	"24": {
		subtitle: "File update not supported by receiver (24)",
		message:
			"Receiver doesn't support file updates. Contact Zeller Support for further assistance.",
	},
	"25": {
		subtitle: "Unable to locate record on file (25)",
		message: "Record not found. Check input details and retry.",
	},
	"26": {
		subtitle: "Duplicate file update record, old record (26)",
		message:
			"Duplicate record. Wait and retry or ask the cardholder to contact their bank if the issue continues.",
	},
	"27": {
		subtitle: "File update field edit error (27)",
		message:
			"Error editing file. Please retry and if the issue persists, please contact Zeller Support for further assistance.",
	},
	"28": {
		subtitle: "File update file locked out (28)",
		message:
			"File locked. Wait and retry or ask the cardholder to contact their bank for help.",
	},
	"29": {
		subtitle: "File update not successful, contact acquirer (29)",
		message:
			"File update failed. Contact Zeller Support for further assistance.",
	},
	"30": {
		subtitle: "Format error (30)",
		message:
			"Data format error. Retry, consider a different payment method or contact Zeller Support for further assistance.",
	},
	"31": {
		subtitle: "Bank not supported by switch (31)",
		message:
			"Card not permitted for non‑face‑to‑face transactions. Instruct the cardholder to contact their bank or consider another payment method.",
	},
	"32": {
		subtitle: "Completed partially (32)",
		message: "Partial transaction completion. Check status and consider retry.",
	},
	"33": {
		subtitle: "Card Expired (33)",
		message:
			"Card expired. Instruct the cardholder to contact their bank or consider another payment method.",
	},
	"34": {
		subtitle: "Suspected fraud (34)",
		message:
			"Issuer suspects fraud. Instruct the cardholder to contact their bank or consider another payment method.",
	},
	"35": {
		subtitle: "Card acceptor contact acquirer (35)",
		message: "Contact your bank for transaction help.",
	},
	"36": {
		subtitle: "Restricted card (36)",
		message:
			"Card has restrictions. Ask the cardholder to contact their bank or consider a different card.",
	},
	"37": {
		subtitle: "Card acceptor call acquirer security (37)",
		message:
			"Issuer declined transaction. Instruct the cardholder to contact their bank or consider another payment method.",
	},
	"38": {
		subtitle: "Allowable PIN tries exceeded (38)",
		message:
			"PIN attempts exceeded. Instruct the cardholder to contact their bank or consider another payment method.",
	},
	"39": {
		subtitle: "No credit account (39)",
		message:
			"No linked credit account. Link a credit account or use a different payment method.",
	},
	"40": {
		subtitle: "Request function not supported (40)",
		message:
			"Requested function not supported. Try a different function or payment method.",
	},
	"41": {
		subtitle: "Card reported as Lost (41)",
		message:
			"Card reported as lost. Instruct the cardholder to contact their bank or consider another payment method.",
	},
	"42": {
		subtitle: "No universal account (42)",
		message:
			"Account type not valid for this card number. Instruct the cardholder to contact their bank or consider another payment method.",
	},
	"43": {
		subtitle: "Stolen card (43)",
		message:
			"Card reported as stolen. Instruct the cardholder to contact their bank or consider another payment method.",
	},
	"44": {
		subtitle: "No investment account (44)",
		message:
			"No linked investment account. Link an investment account or use a different payment method.",
	},
	"51": {
		subtitle: "Insufficient funds (51)",
		message:
			"Insufficient funds on card. Instruct the cardholder to contact their bank or consider another payment method.",
	},
	"52": {
		subtitle: "No cheque account (52)",
		message:
			"No linked cheque account. Link a cheque account or use a different payment method.",
	},
	"53": {
		subtitle: "No savings account (53)",
		message:
			"No linked savings account. Link a savings account or use a different payment method.",
	},
	"54": {
		subtitle: "Card Expired (54)",
		message:
			"Card expired. Instruct the cardholder to contact their bank or consider another payment method.",
	},
	"55": {
		subtitle: "Incorrect Pin (55)",
		message: "Incorrect PIN. Ask the cardholder to re‑enter PIN.",
	},
	"56": {
		subtitle: "No card record (56)",
		message:
			"Card number doesn't exist. Instruct the cardholder to contact their bank or consider another payment method.",
	},
	"57": {
		subtitle: "Transaction not permitted to cardholder (57)",
		message:
			"Card not permitted for this transaction type. Instruct the cardholder to insert their card and select their account or consider another payment method.",
	},
	"58": {
		subtitle: "Transaction not permitted to terminal (58)",
		message:
			"Transaction not permitted to this terminal. Try a different terminal or payment method.",
	},
	"59": {
		subtitle: "Suspected fraud (59)",
		message:
			"Transaction appears fraudulent. Instruct the cardholder to contact their bank or consider another payment method.",
	},
	"60": {
		subtitle: "Card acceptor contact acquirer (60)",
		message: "Contact your bank for transaction help.",
	},
	"61": {
		subtitle: "Exceeds withdrawal amount limits (61)",
		message:
			"Transaction exceeds card limit. Instruct the cardholder to contact their bank or consider another payment method.",
	},
	"62": {
		subtitle: "Restricted card (62)",
		message:
			"Card not valid in certain region/country. Instruct the cardholder to contact their bank or consider another payment method.",
	},
	"63": {
		subtitle: "Security violation (63)",
		message: "Security breach suspected. Contact your bank for help.",
	},
	"64": {
		subtitle: "Original amount incorrect (64)",
		message: "Incorrect original amount. Check amount and retry.",
	},
	"65": {
		subtitle: "Retry with card inserted (65)",
		message:
			"Trouble reading card. Ask the cardholder to re‑insert card and retry.",
	},
	"66": {
		subtitle: "Card acceptor call acquirer security dept (66)",
		message: "Security issue suspected. Contact your bank for help.",
	},
	"67": {
		subtitle: "Hard capture (Pick‑up card (67)",
		message:
			"Card needs to be picked up. Contact your bank for help and consider a different payment method.",
	},
	"68": {
		subtitle: "Response received too late (68)",
		message: "Late response received. Retry transaction.",
	},
	"72": {
		subtitle: "Account not yet activated (72)",
		message:
			"Account not yet activated. Instruct the cardholder to contact their bank or consider another payment method.",
	},
	"75": {
		subtitle: "Allowable number of PIN tries exceeded (75)",
		message:
			"PIN attempts exceeded. Wait and retry or ask the cardholder to contact their bank for help.",
	},
	"91": {
		subtitle: "Issuer or switch is inoperative (91)",
		message:
			"Problem contacting issuing bank. Retry transaction or instruct the cardholder to contact their bank if the problem continues.",
	},
	"92": {
		subtitle: "Routing error (92)",
		message:
			"Transaction routing error. Retry transaction or contact your bank if the issue persists.",
	},
	"93": {
		subtitle: "Transaction cannot be completed (Violation of law (93)",
		message:
			"Transaction violates law. Try a different transaction or payment method.",
	},
	"94": {
		subtitle: "Duplicate transmission (94)",
		message:
			"Duplicate transmission. Wait and retry or contact your bank if the issue continues.",
	},
	"96": {
		subtitle: "System malfunction (96)",
		message:
			"System malfunction. Retry transaction or contact your bank if the issue continues.",
	},
	"98": {
		subtitle: "Invalid MAC (98)",
		message: "Invalid MAC. Check MAC and retry or contact your bank for help.",
	},
	"1000": {
		subtitle: "User Cancelled (1000)",
		message: "The payment was cancelled. Try processing the transaction again.",
	},
	"1001": {
		subtitle: "Response Timeout (1001)",
		message:
			"No response was received in the time allowed. Try processing the transaction again.",
	},
	"1002": {
		subtitle: "Card Declined (1002)",
		message:
			"The card declined the transaction. Refer the cardholder to their bank or seek another form of payment.",
	},
	"1003": {
		subtitle: "Signature Mismatch (1003)",
		message:
			"Signature declined. The signature provided did not match that on the card.",
	},
	"1004": {
		subtitle: "Communications Failure (1004)",
		message:
			"Could not process the transaction due to communications failure. Check Internet connection and try processing the transaction again.",
	},
	"1005": {
		subtitle: "Invalid response (1005)",
		message:
			"The response received was invalid. Try processing the transaction again.",
	},
	"1006": {
		subtitle: "Pending Transaction Failed (1006)",
		message:
			"Could not process the transaction due to a failed pending transaction. Try processing the transaction again.",
	},
	"1007": {
		subtitle: "System Error (1007)",
		message:
			"Could not process the transaction due to a system error. Try processing the transaction again.",
	},
	"1009": {
		subtitle: "Insufficient Funds (1009)",
		message:
			"Insufficient funds on card. Instruct the cardholder to contact their bank or consider another payment method.",
	},

	// legacy P‑codes
	"P2": {
		title: "Card Read Failure",
	},
	"P3": {
		title: "User cancelled transaction",
	},
	"P4": {
		title: "Terminal cancelled transaction",
	},
	"P5": {
		title: "User response timeout",
	},

	// new Q‑codes
	"Q1": {
		title: "Transaction Cancelled",
		message: "The transaction has been cancelled.",
	},
	"Q2": {
		title: "Transaction timed out",
		message: "The transaction has timed out due to no activity.",
	},
	"Q3": {
		subtitle: "Try a physical card",
		message:
			"There was a problem processing this Visa mobile payment transaction. Please try again using a physical card.",
	},
	"Q4": {
		subtitle: "Try a different card",
		message:
			"There was a problem processing this EFTPOS card transaction. Try again with a different card, such as Visa or Mastercard.",
	},
	"Q5": {
		message: "There was a problem processing this card transaction.",
	},
	"Q6": {
		message:
			"Something went wrong when trying to process your payment. Please try again.",
	},
	"Q7": {
		subtitle: "Signature Mismatch",
		message:
			"Signature declined. The signature provided did not match that on the card.",
	},
	"Q8": {
		title: "Failed",
		subtitle: "Communications Failure",
		message:
			"No response was received in the time allowed. Try processing the transaction again.",
	},
	"Q9": {
		title: "Failed",
		subtitle: "Terminal Busy",
		message:
			"The terminal is currently busy. Please check the terminal and try again.",
	},
	"QA": {
		title: "Failed",
		subtitle: "Invalid response",
		message:
			"The response received was invalid. Try processing the transaction again.",
	},
	"QB": {
		title: "Declined",
		message:
			"The card declined the transaction. Refer the cardholder to their bank or seek another form of payment.",
	},
	"QC": {
		title: "Failed",
		subtitle: "System Error",
		message:
			"Could not process the transaction due to a system error. Try processing the transaction again.",
	},
	"QD": {
		title: "Failed",
		subtitle: "Card Blocked",
		message:
			"Card is blocked. Refer the cardholder to their bank or seek another form of payment.",
	},
	"QE": {
		title: "Declined",
		subtitle: "Card is not supported",
		message: "The card is not supported. Seek another form of payment.",
	},
	"QF": {
		title: "Declined",
		subtitle: "Could not read card",
		message: "The card could not be read. Seek another form of payment.",
	},
	"QG": {
		title: "Declined",
		subtitle: "Card needs to be inserted",
		message: "The card needs to be inserted. Please try again.",
	},
	"QH": {
		title: "Declined",
	},
};
